import { FC } from 'react'
import { Box, Button, styled, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Modal } from 'components/Modal'
import { useTimeConstrainedControl } from 'components/TimeConstrainedControl'
import { useAccessRequestPolicy } from 'hooks'
import { isNilOrEmpty } from 'packages/core'

const FooterContainer = styled(Box)(() => ({
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 24px',
}))

export interface ActivateConfirmModalProps {
    open: boolean
    onClose: () => void
    onConfirm: (timeConstraintDetails: {
        timeConstrained: boolean
        startDateUtc: any
        endDateUtc: any
    }) => void
    resourceName: string
    policyId?: string
    disabled?: boolean
    proceeding?: boolean
}

export const ActivateConfirmModal: FC<ActivateConfirmModalProps> = ({
    open,
    onClose,
    onConfirm,
    resourceName,
    policyId = '',
    disabled = false,
    proceeding = false,
}) => {
    const { t } = useTranslation()
    const theme = useTheme()

    // Fetch policy using the policyId prop
    const policy = useAccessRequestPolicy(policyId)

    // When policy requires time constraints, hide the switch but show the control
    const shouldHideSwitch = policy?.isTimeConstrained ?? false
    const {
        timeConstrained,
        selectedStartDate,
        selectedEndDate,
        hasInvalidDates,
        violatesPolicy,
        timeConstrainedControl,
    } = useTimeConstrainedControl(policy, shouldHideSwitch, disabled, shouldHideSwitch, false)

    const handleConfirm = () => {
        // If policy requires time constraints, always include them
        const isTimeConstrainedRequired = policy?.isTimeConstrained ?? false
        const shouldIncludeTimeConstraint = timeConstrained || isTimeConstrainedRequired

        const timeConstraintDetails = {
            timeConstrained: shouldIncludeTimeConstraint,
            startDateUtc: shouldIncludeTimeConstraint ? selectedStartDate.utc().format() : null,
            endDateUtc: shouldIncludeTimeConstraint ? selectedEndDate?.utc().format() : null,
        }
        onConfirm(timeConstraintDetails)
    }

    const isActivateDisabled = hasInvalidDates || proceeding || disabled

    const footerContent = (
        <FooterContainer>
            <Button
                variant="outlined"
                onClick={onClose}
                disabled={proceeding}
                sx={{
                    textTransform: 'none',
                    borderColor: theme?.palette?.grey?.[400],
                    color: theme?.palette?.text?.primary,
                    '&:hover': {
                        borderColor: theme?.palette?.grey?.[600],
                    },
                }}
            >
                {t('Common_Cancel')}
            </Button>
            <Button
                variant="contained"
                onClick={handleConfirm}
                disabled={isActivateDisabled}
                sx={{
                    textTransform: 'none',
                    backgroundColor: theme?.palette?.secondary?.main,
                    color: theme?.palette?.common?.white,
                    '&:hover': {
                        backgroundColor: theme?.palette?.secondary?.dark,
                    },
                    '&:disabled': {
                        backgroundColor: theme?.palette?.grey?.[300],
                        color: theme?.palette?.grey?.[500],
                    },
                }}
            >
                {t('Activate')}
            </Button>
        </FooterContainer>
    )

    const renderPolicyDetails = () => {
        if (!policy || (!policy.isTimeConstrained && !policy.maximumValueInMinutes && !policy.defaultValueInMinutes)) {
            return null
        }

        return (
            <Box>

                {policy.friendlyName && (
                    <Typography variant="body2" sx={{ marginBottom: '4px' }}>
                        <strong>{t('Common_AccessRequestPolicy')}:</strong> {policy.friendlyName}
                    </Typography>
                )}
                {policy.isTimeConstrained !== undefined && (
                    <Typography variant="body2" sx={{ marginBottom: '4px' }}>
                        <strong>{t('Common_AllowsTimeConstrainedAccess')}:</strong>{' '}
                        {policy.isTimeConstrained ? t('Common_Yes') : t('Common_No')}
                    </Typography>
                )}
                {!isNilOrEmpty(policy.defaultValueInMinutes) && (
                    <Typography variant="body2" sx={{ marginBottom: '4px' }}>
                        <strong>{t('Common_DefaultAccessDuration')}:</strong>{' '}
                        {policy.defaultValueInMinutes}
                    </Typography>
                )}
                {!isNilOrEmpty(policy.maximumValueInMinutes) && (
                    <Typography variant="body2">
                        <strong>{t('Common_MaxAccessDuration')}:</strong>{' '}
                        {policy.maximumValueInMinutes}
                    </Typography>
                )}
            </Box>
        )
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={t('Activate') + ' ' + resourceName}
            footerContent={footerContent}
            disableClose={proceeding}
        >
            <Box sx={{ minWidth: '50rem' }}>
                {renderPolicyDetails()}
                <br />
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '16px',
                    }}
                >
                    {timeConstrainedControl}
                </Box>
                {hasInvalidDates && !violatesPolicy && (
                    <Typography
                        variant="caption"
                        color="error"
                        sx={{ marginTop: '8px', display: 'block' }}
                    >
                        {t('Common_InvalidDates')}
                    </Typography>
                )}
            </Box>
        </Modal>
    )
}
